import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  NumberInput,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { CrudSection } from '../components/CrudSection';
import { useLocationId } from '../hooks/useSeedLocation';
import { enumOptions, humanize } from '../lib/enums';
import { formatDateTime } from '../lib/format';
import { notifyError, notifySaved } from '../lib/notify';

const SETTINGS_PATH = '/admin/v1/admin/settings';

interface Setting {
  key: string;
  value: unknown;
  updatedAt?: string | null;
}

/**
 * How one stored value — or one field inside a stored value — is shown and edited.
 *
 * These settings have no schema of their own: each row is a key and whatever JSON was put there.
 * So the editor is chosen from the shape of the value that is already stored, and it writes back
 * that same shape. That matters more than it sounds: several of these flags are stored as the
 * integers 0 and 1 rather than as true/false, and a helpful "tidy-up" to booleans would be a
 * silent change to how the registers read them.
 */
interface FieldInfo {
  label: string;
  description?: string;
  /** An on/off choice. Stored as 0/1 or as true/false; whichever it is, it stays that way. */
  flag?: boolean;
  /** Whole amount of money, stored in the smallest unit. */
  money?: boolean;
  /** The setting that says which money the amount above is in. */
  currencyKey?: string;
  suffix?: string;
  options?: { value: string; label: string }[];
}

interface SettingInfo extends FieldInfo {
  /** Editors for the fields inside an object value, keyed by field name. */
  fields?: Record<string, FieldInfo>;
}

/**
 * SETTINGS THAT ARE STORED, AND THAT NOTHING READS. They do not appear on this screen at all.
 *
 * A control that moves and changes nothing is worse than a missing feature, because the owner acts
 * on it and stops looking for the real answer. Five of these were on the screen: a switch labelled
 * "Charge the tourism levy" that no code consulted, a "flag a tab left open for" that produced no
 * list, a per-station ticket language with no column to put it in, and two payment flags whose
 * features are not built. All of them looked exactly like the settings that work.
 *
 * `scripts/check-settings-readers.mjs` reads this set and the seed and fails the build in both
 * directions: a key that reaches the screen with no reader in `backend/src`, and a key on this list
 * that has since grown one — because a rule the owner is governed by and cannot see is the other
 * half of the same fault.
 *
 * What each one would take:
 *
 * - `fiscal.tourismComponentEnabled` — deliberately NOT wired, and it should not be. `componentsFor`
 *   is also what REBUILDS a historical document, so a global boolean read there would re-rate last
 *   year's invoices the moment it was switched — the exact thing TAX-01's effective-dated rates
 *   exist to prevent. The way to stop charging a levy from a date is to give its component a 0 bp
 *   rate from that date, which the Invoicing screen already does; that screen now says so.
 * - `tabs.staleTabDays` — needs POS-08's stale-tab report: a list of tabs open longer than this,
 *   with somewhere to chase or write one off.
 * - `kds.stationLanguages` — needs a language column on `kds_tickets` and a `fire.ts` that reads it.
 * - `fiscal.invoiceDepositsOnReceipt`, `bitcoin.mode` — Phase 2 features. Nothing to switch yet.
 * - `referrals.commissionableBase` — this one was on the screen until the check learned to tell code
 *   from a comment about code, and it was the worst of them, because it was not a switch with no
 *   effect: it was a switch with a rival. It read *"Applies to every tour operator, driver and
 *   agency unless their own agreement says otherwise"*, and `domains/referrals/commission.ts` is
 *   explicit that **the plan is the only authority** — a partner with no plan earns nothing and is
 *   reported as skipped, precisely so that nobody is ever paid on a base nobody chose. So the owner
 *   could set "leave alcohol out" here, see it saved, and every commission this bar pays would go on
 *   including alcohol, with the screen telling him otherwise. What it would take is what the comment
 *   in that file already says it is: the STARTING VALUES the new-commission-plan form is filled in
 *   with, which is a portal feature and belongs beside that form, not a rule anything resolves at
 *   payout time.
 */
const HIDDEN_UNTIL_SOMETHING_READS_IT = new Set([
  'fiscal.tourismComponentEnabled',
  'tabs.staleTabDays',
  'kds.stationLanguages',
  'fiscal.invoiceDepositsOnReceipt',
  'bitcoin.mode',
  'referrals.commissionableBase',
]);

const SETTINGS: Record<string, SettingInfo> = {
  'tabs.globalCreditLimitMinor': {
    label: 'Tab limit',
    description: 'How much a guest can run up before the register stops adding to their tab.',
    money: true,
    currencyKey: 'tabs.globalCreditLimitCurrency',
  },
  'tabs.globalCreditLimitCurrency': {
    label: 'Tab limit is in',
    // The amount above is a bare number with this setting deciding how to read it, so switching
    // money does not convert anything: a $200 limit becomes L 200 — about eight dollars — and the
    // only thing on screen that changes is the symbol in front of it.
    description:
      'This does not convert the amount above. The same number is read as the new money, so set the limit again after changing it.',
    options: enumOptions('currency'),
  },
  'cash.ownerChecks': {
    /*
     * This was 'cash.secondVerifierRequired' — "A second person must witness" — and it demanded a
     * witness who does not exist: one bartender works a shift, and staff have no accounts here. Its
     * switches refused every night drop and held every drawer open behind an unverified count. They
     * now decide what appears on the owner's own list to look at, which is a thing he can act on.
     */
    label: 'Put these on my list to check',
    description:
      'Counts you want to look at yourself, on the register screen. Nothing waits for you — the bar goes on trading and the list is there when you are.',
    fields: {
      openingCount: { label: 'The opening float count' },
      closingCounts: { label: 'The closing count' },
      nightDrops: { label: 'Money dropped to the safe at night' },
      safeCounts: { label: 'Safe and float box counts' },
    },
  },
  'fiscal.rangeWarnAtRemaining': {
    label: 'Warn when a register has this many invoice numbers left',
    description:
      'A replacement CAI takes days to obtain, not minutes, so the warning has to arrive well before the last number.',
    suffix: ' numbers',
  },
  'fiscal.rangeWarnAtDays': {
    label: 'Warn this long before a CAI stops working',
    description: 'How much notice you want of the date a range can no longer be printed from.',
    suffix: ' days',
  },
  'staffMeal.dailyCapMinor': {
    label: 'Staff meal allowance per person, per day',
    description:
      'In dollars. How much staff-meal discount one person can be given in one business day, across every check. The register refuses past it rather than trimming; a lempira item counts at the day’s rate.',
    money: true,
  },
  'staffMeal.maxLinesPerCheck': {
    label: 'Staff-meal items per check',
    description:
      'The most items one person’s staff meal can cover on one check. The next one is somebody else’s bill.',
    suffix: ' items',
  },
  'staffMeal.requiresClockIn': {
    label: 'Staff meal needs the person on the clock',
    description:
      'Off, the register records where their clock stood and the report shows it. Turn it on once punching in is a habit — on, a meal for somebody off the clock is refused.',
    flag: true,
  },
  'giveaways.performerDailyCapMinor': {
    label: 'Given away per bartender, per day',
    description:
      'In dollars. A ceiling on one person’s ordinary comps, discounts and price breaks in one business day — several small ones add up to it. Staff meals and the house tab have their own rules and do not count.',
    money: true,
  },
};

const GROUPS: { title: string; description: string; keys: string[] }[] = [
  {
    title: 'Guest tabs',
    description: 'How far a guest can run a tab, and in which money.',
    keys: ['tabs.globalCreditLimitMinor', 'tabs.globalCreditLimitCurrency'],
  },
  {
    title: 'Cash handling',
    description: 'What you want to check yourself, and what you are happy to leave alone.',
    keys: ['cash.ownerChecks'],
  },
  {
    title: 'Tax and invoicing',
    description: 'How much warning you get before a register runs out of invoice numbers.',
    keys: ['fiscal.rangeWarnAtRemaining', 'fiscal.rangeWarnAtDays'],
  },
  {
    title: 'Staff meals and giveaways',
    description:
      'The daily numbers behind the staff discount and the comp buttons. What the meal covers is set on the Pricing page; these are the amounts the register refuses past.',
    keys: [
      'staffMeal.dailyCapMinor',
      'staffMeal.maxLinesPerCheck',
      'staffMeal.requiresClockIn',
      'giveaways.performerDailyCapMinor',
    ],
  },
];

/**
 * Settings that belong on a screen of their own, and must not also appear here.
 *
 * The catch-all "Everything else" section exists so a setting added later is never invisible. The
 * cost of that is a value with a purpose-built editor elsewhere getting a second, generic editor
 * here — two boxes for one number, disagreeing until one of them is saved. The customer-RTN
 * threshold is set beside the CAI paperwork on the Invoicing screen, where the person setting it is
 * already looking. The day the clock takes over is set on the hours sheet, for the same reason: it
 * is decided while looking at the days it governs, and a bare date typed here would be a promise
 * about overtime made a long way from the hours it changes.
 */
const EDITED_ELSEWHERE = new Set([
  'fiscal.customerRtnRequiredAboveMinor',
  'fiscal.customerRtnRequiredCurrency',
  'payroll.timesRequiredFrom',
]);

const CURRENCY_PREFIX: Record<string, string> = { HNL: 'L ', USD: '$ ' };

/**
 * Reasons that are not about an item at all.
 *
 * Whether the stock comes back is a question you can only ask about something that was made or
 * poured. Cash handed out of the drawer for a taxi, and a deposit given back to a guest, move no
 * stock whatsoever — yet the column answered "Stock is used up" for both, which is a statement
 * about stock that never moved, printed against every row of the two kinds.
 */
const STOCKLESS_REASON_KINDS = new Set(['paidOut', 'depositRefund']);

/**
 * One editable value. Everything it writes back keeps the type it read, which is what lets a
 * setting be edited at all without a schema to check it against.
 */
function ValueField({
  info,
  value,
  currency,
  onChange,
}: {
  info: FieldInfo;
  value: unknown;
  currency?: string;
  onChange: (next: unknown) => void;
}) {
  if (info.options) {
    return (
      <Select
        label={info.label}
        description={info.description}
        data={info.options}
        value={typeof value === 'string' ? value : null}
        onChange={(next) => next !== null && onChange(next)}
        allowDeselect={false}
        comboboxProps={{ withinPortal: true }}
        maw={320}
      />
    );
  }

  // A 0 or a 1 is only read as a switch when this key is known to be one. Guessing from the
  // number alone would turn a genuine "1 day" into an on/off toggle.
  if (info.flag || typeof value === 'boolean') {
    const on = value === true || value === 1;
    return (
      <Switch
        label={info.label}
        description={info.description}
        checked={on}
        onChange={(e) =>
          onChange(typeof value === 'number' ? (e.currentTarget.checked ? 1 : 0) : e.currentTarget.checked)
        }
      />
    );
  }

  if (typeof value === 'number') {
    return (
      <NumberInput
        label={info.label}
        description={info.description}
        value={info.money ? value / 100 : value}
        onChange={(next) => {
          const n = typeof next === 'string' ? Number(next) : next;
          if (!Number.isFinite(n)) return;
          onChange(info.money ? Math.round(n * 100) : Math.round(n));
        }}
        decimalScale={info.money ? 2 : 0}
        fixedDecimalScale={info.money}
        thousandSeparator={info.money ? ',' : undefined}
        prefix={info.money ? (CURRENCY_PREFIX[currency ?? 'HNL'] ?? '') : undefined}
        suffix={info.suffix}
        hideControls={info.money}
        min={0}
        maw={320}
      />
    );
  }

  // Anything else — a free-form string with no list of allowed answers, a list, a shape within a
  // shape — is shown but not edited. Guessing at what a valid replacement looks like is how a
  // setting gets a value the registers silently ignore.
  return (
    <div>
      <Text size="sm" fw={500}>
        {info.label}
      </Text>
      <Text size="sm">{value == null ? '—' : humanize(String(value))}</Text>
      <Text size="xs" c="dimmed">
        Set up when the system was installed. Ask for help to change this.
      </Text>
    </div>
  );
}

function SettingCard({
  setting,
  info,
  currency,
}: {
  setting: Setting;
  info: SettingInfo;
  currency?: string;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<unknown>(setting.value);

  const isObject =
    typeof setting.value === 'object' && setting.value !== null && !Array.isArray(setting.value);
  const changed = JSON.stringify(draft) !== JSON.stringify(setting.value);

  const save = useMutation({
    mutationFn: () => rawApi('PUT', `${SETTINGS_PATH}/${encodeURIComponent(setting.key)}`, { value: draft }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(SETTINGS_PATH) });
      notifySaved(info.label);
    },
    onError: (err) => notifyError(err, `save "${info.label.toLowerCase()}"`),
  });

  return (
    <Stack gap="xs">
      {isObject ? (
        <>
          <div>
            <Text size="sm" fw={500}>
              {info.label}
            </Text>
            {info.description && (
              <Text size="xs" c="dimmed">
                {info.description}
              </Text>
            )}
          </div>
          <Stack gap="xs" pl="md">
            {Object.entries(draft as Record<string, unknown>).map(([field, fieldValue]) => (
              <ValueField
                key={field}
                info={info.fields?.[field] ?? { label: humanize(field) }}
                value={fieldValue}
                onChange={(next) =>
                  // Spread the stored object so untouched fields keep their exact value and order.
                  setDraft({ ...(draft as Record<string, unknown>), [field]: next })
                }
              />
            ))}
          </Stack>
        </>
      ) : (
        <ValueField info={info} value={draft} currency={currency} onChange={setDraft} />
      )}

      <Group gap="sm">
        {changed && (
          <>
            <Button size="xs" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
            <Button
              size="xs"
              variant="subtle"
              disabled={save.isPending}
              onClick={() => setDraft(setting.value)}
            >
              Undo
            </Button>
          </>
        )}
        {!changed && setting.updatedAt && (
          <Text size="xs" c="dimmed">
            Last changed {formatDateTime(setting.updatedAt)}
          </Text>
        )}
      </Group>
    </Stack>
  );
}

/**
 * Purpose-built rather than a table, because these are not records: there is no list to add to and
 * nothing to delete — just a fixed set of house rules, each of which someone has to be able to
 * change without knowing what shape it is stored in.
 */
function HouseRules() {
  const settings = useQuery({
    queryKey: listKey(SETTINGS_PATH),
    queryFn: () => rawApi<Setting[]>('GET', SETTINGS_PATH),
  });

  if (settings.isLoading) {
    return (
      <Group gap="xs" py="xl" justify="center">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading…
        </Text>
      </Group>
    );
  }

  if (settings.isError) {
    return (
      <Alert color="red" title="Could not load your house rules">
        <Text size="sm" mb="sm">
          Nothing has changed — this is only a display problem.
        </Text>
        <Button size="xs" variant="light" onClick={() => void settings.refetch()}>
          Try again
        </Button>
      </Alert>
    );
  }

  const rows = settings.data ?? [];
  const byKey = new Map(rows.map((s) => [s.key, s]));
  const grouped = new Set(GROUPS.flatMap((g) => g.keys));
  const ungrouped = rows.filter(
    (s) => !grouped.has(s.key) && !EDITED_ELSEWHERE.has(s.key) && !HIDDEN_UNTIL_SOMETHING_READS_IT.has(s.key),
  );

  const sections = [
    ...GROUPS.map((g) => ({
      ...g,
      rows: g.keys.map((k) => byKey.get(k)).filter((s): s is Setting => Boolean(s)),
    })),
    // Anything added later still appears, rather than being invisible until this file catches up.
    ...(ungrouped.length
      ? [{ title: 'Everything else', description: 'Other rules this system follows.', rows: ungrouped }]
      : []),
  ].filter((s) => s.rows.length > 0);

  return (
    <>
      <Text size="sm" c="dimmed" mb="md">
        The rules the registers and the kitchen follow. A change takes effect at each register's next sync.
      </Text>
      {sections.map((section) => (
        <Paper key={section.title} withBorder p="md" radius="md" mb="md">
          <Title order={5}>{section.title}</Title>
          <Text size="sm" c="dimmed" mt={2} mb="md">
            {section.description}
          </Text>
          <Stack gap="lg">
            {section.rows.map((setting) => {
              const info = SETTINGS[setting.key] ?? {
                label: humanize(setting.key.split('.').pop() ?? setting.key),
              };
              const currency = info.currencyKey
                ? String(byKey.get(info.currencyKey)?.value ?? '')
                : undefined;
              return (
                <SettingCard
                  // Re-seeds the editor from the server after a save, so the two can never drift.
                  key={`${setting.key}:${setting.updatedAt ?? ''}`}
                  setting={setting}
                  info={info}
                  currency={currency}
                />
              );
            })}
          </Stack>
        </Paper>
      ))}
    </>
  );
}

/**
 * The owner's own PIN at the register.
 *
 * The owner is not an employee — no row on the staff page, nothing on payroll — but he walks up to
 * a register like anybody else: to sign in, count a shelf, pull a drawer report, or demo the
 * kitchen screen. This is where he sets the PIN that lets him, AND HE CHOOSES IT HIMSELF: the
 * generated-then-change discipline on staff PINs exists to retire the setter's knowledge, and here
 * the setter and the holder are the same person. It answers to the same uniqueness rule staff PINs
 * do, refused the same way.
 */
function OwnerPin({ locationId }: { locationId: string | null | undefined }) {
  const [pin, setPin] = useState('');
  const valid = /^\d{4,6}$/.test(pin);

  const save = useMutation({
    mutationFn: () => rawApi('POST', '/admin/v1/staff/owner-pin', { locationId, pin }),
    onSuccess: () => {
      setPin('');
      notifySaved('Your PIN is set. It signs you in at every register, with everything available.');
    },
    onError: (err) => notifyError(err, 'set your PIN'),
  });

  return (
    <Stack gap="xs" mb="lg">
      <Title order={5}>Your PIN at the register</Title>
      <Text size="sm" c="dimmed" maw={720}>
        You are not on the staff list and never on payroll, but your PIN signs you in at any register with
        everything available — the register itself, the kitchen screen, stock, reports, the drawer. Pick the
        one you want; setting it again replaces it everywhere at once.
      </Text>
      <Group align="flex-end">
        <PasswordInput
          label="Your PIN"
          description="4 to 6 digits"
          value={pin}
          onChange={(e) => setPin(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          error={pin.length > 0 && !valid ? 'Use 4 to 6 digits' : undefined}
          w={220}
        />
        <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!valid || !locationId}>
          Set my PIN
        </Button>
      </Group>
    </Stack>
  );
}

export function SettingsPage() {
  const locationId = useLocationId();
  return (
    <>
      <Title order={3} mb="md">
        Settings
      </Title>
      <Tabs defaultValue="settings" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="settings">House rules</Tabs.Tab>
          <Tabs.Tab value="reasons">Reasons</Tabs.Tab>
          <Tabs.Tab value="banks">Bank accounts</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="settings">
          <OwnerPin locationId={locationId} />
          <HouseRules />
        </Tabs.Panel>

        <Tabs.Panel value="reasons">
          <CrudSection
            title="Reasons"
            entityNoun="reason"
            description="The reasons staff pick from when something is cancelled, given away, remade or thrown out. Keep the list short so the right one is easy to find — stop offering the ones nobody uses rather than adding more."
            listPath="/admin/v1/admin/reason-codes"
            updatePathTemplate="/admin/v1/admin/reason-codes/{id}"
            emptyMessage="No reasons yet. Add the ones your staff need most."
            rowLabel={(r) => String(r.nameEn ?? 'this reason')}
            columns={[
              { accessor: 'kind', title: 'Used for', enumGroup: 'reasonCodeKind' },
              { accessor: 'nameEn', title: 'English' },
              { accessor: 'nameEs', title: 'Spanish' },
              // Yes-or-blank in both of these last two columns answered a question nobody had:
              // almost every reason does not restock, and almost every reason is in use, so two
              // columns of empty cells hid the handful of rows that are actually different.
              {
                accessor: 'restocksInventory',
                title: 'Effect on stock',
                render: (r) =>
                  STOCKLESS_REASON_KINDS.has(String(r.kind)) ? (
                    <Text size="sm" c="dimmed">
                      No stock involved
                    </Text>
                  ) : r.restocksInventory ? (
                    <Text size="sm">Goes back into stock</Text>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Stock is used up
                    </Text>
                  ),
              },
              {
                accessor: 'active',
                title: '',
                render: (r) =>
                  r.active === false ? (
                    <Badge color="gray" variant="light">
                      Not offered
                    </Badge>
                  ) : null,
              },
            ]}
            fields={[
              {
                name: 'kind',
                label: 'Used for',
                type: 'select',
                required: true,
                fixedAfterCreate: true,
                options: enumOptions('reasonCodeKind'),
              },
              // Nothing checked this, and it can never be corrected: "Broken Glass" and
              // "broken glass" both saved happily, and from then on that is the name the stock
              // ledger and the register write against every bottle dropped. The rule is said in the
              // box now rather than discovered a year later in a report nobody can group.
              {
                name: 'code',
                label: 'Short name',
                type: 'text',
                required: true,
                fixedAfterCreate: true,
                placeholder: 'broken-glass',
                description:
                  'Lower-case words joined by hyphens. Staff never see this, and it cannot change afterwards — every waste and refund already recorded is filed under it.',
                validate: (value) =>
                  /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(String(value ?? ''))
                    ? null
                    : 'Lower-case letters, numbers and hyphens only — such as broken-glass',
              },
              { name: 'nameEn', label: 'What staff see in English', type: 'text', required: true },
              { name: 'nameEs', label: 'What staff see in Spanish', type: 'text', required: true },
              {
                name: 'restocksInventory',
                label: 'The item goes back into stock',
                type: 'boolean',
                description:
                  'Tick this when nothing was actually used — a mistaken order, not a spillage. It does nothing for cash paid out of the drawer or a returned deposit, where no item is involved.',
              },
              {
                name: 'active',
                label: 'Offer this reason to staff',
                type: 'boolean',
                defaultValue: true,
                description:
                  'Untick to retire it. Nothing already recorded against it changes — it just stops being offered.',
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="banks">
          <CrudSection
            title="Bank accounts"
            entityNoun="bank account"
            description="The accounts a guest or a delivery platform can pay into. Staff choose from this list when they record a transfer, so an account you have closed at the bank must be stopped here too."
            listPath="/admin/v1/admin/bank-accounts"
            updatePathTemplate="/admin/v1/admin/bank-accounts/{id}"
            defaults={{ locationId }}
            rowLabel={(r) => String(r.accountLabel ?? r.bankName ?? 'this account')}
            emptyMessage="No accounts yet. Add one so staff can record bank transfers."
            columns={[
              { accessor: 'bankName', title: 'Bank' },
              { accessor: 'accountLabel', title: 'Account' },
              { accessor: 'currency', title: 'Money', enumGroup: 'currency' },
              // Every account is in use until one is not, so a column of "Yes" said nothing. Only
              // the exception is worth a cell.
              {
                accessor: 'active',
                title: '',
                render: (r) =>
                  r.active === false ? (
                    <Badge color="gray" variant="light">
                      Not offered
                    </Badge>
                  ) : null,
              },
            ]}
            fields={[
              { name: 'bankName', label: 'Bank', type: 'text', required: true },
              {
                name: 'accountLabel',
                label: 'Account name',
                type: 'text',
                required: true,
                description: 'How staff will recognise it — the last four digits are usually enough',
              },
              {
                name: 'currency',
                label: 'Money the account holds',
                type: 'select',
                required: true,
                fixedAfterCreate: true,
                options: enumOptions('currency'),
                description:
                  'Fixed once saved — transfers already recorded here are amounts in this money, and changing it would re-read every one of them.',
              },
              { name: 'active', label: 'Offer this account to staff', type: 'boolean', defaultValue: true },
            ]}
          />
        </Tabs.Panel>

        {/* Guests moved to the Customers page under People: a customer record stopped being an
            address-book curiosity the day the register learned to put a tab on one. */}
      </Tabs>
    </>
  );
}
